import { inject } from '@angular/core';
import { type Route } from '@angular/router';
import {
  dataImportLayoutConfig,
  dataManagementLayoutConfig,
  manageAttributesLayoutConfig,
} from '@bizzdesign/feature-layout';
import { FeatureFlagsStore } from '@bizzdesign/shared/feature-flags';
import { baseRoutes, canAccess, canRedirect } from '@bizzdesign/shared/utils';
import { type DataEditorContainerComponent } from './containers/data-editor-container/data-editor-container.component';

export const FEATURE_DATA_MANAGEMENT_ROUTES: Route[] = [
  {
    path: baseRoutes.dataManagement,
    pathMatch: 'full',
    redirectTo: canRedirect(
      'dataManagement',
      'manage',
      /* onPermit */
      canRedirect(
        'attributes',
        'manage',
        /* onPermit */
        baseRoutes.manageAttributes,
        /* onForbid */
        canRedirect(
          'dataImport',
          'manage',
          /* onPermit */
          baseRoutes.dataImport,
        ),
      ),
    ),
  },
  {
    path: baseRoutes.dataImport,
    loadComponent: () => {
      const featureFlags = inject(FeatureFlagsStore);
      if (featureFlags.dataImportV2Enabled()) {
        return import(
          './containers/import-data-container/import-data-container.component'
        ).then((mod) => mod.ImportDataContainerComponent);
      }

      return import(
        './containers/data-import-container/data-import-container.component'
      ).then((mod) => mod.DataImportContainerComponent);
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: () => {
          const featureFlags = inject(FeatureFlagsStore);
          return featureFlags.dataImportV2Enabled() ? 'objects' : '';
        },
      },
      {
        path: 'objects',
        loadComponent: () =>
          import(
            './containers/import-objects-container/import-objects-container.component'
          ).then((m) => m.ImportObjectsContainerComponent),
      },
      {
        path: 'relations',
        loadComponent: () =>
          import(
            './containers/import-relations-container/import-relations-container.component'
          ).then((m) => m.ImportRelationsContainerComponent),
      },
    ],
    data: {
      layoutConfig: dataImportLayoutConfig,
    },
    canActivate: [canAccess('dataImport', 'manage')],
  },
  {
    path: 'manage-attributes-2',
    canMatch: [() => inject(FeatureFlagsStore).manageAttributesEnabled()],
    loadComponent: () =>
      import(
        './containers/manage-attributes-container-2/manage-attributes-container.component'
      ).then((mod) => mod.ManageAttributesContainerComponent),
    data: {
      layoutConfig: manageAttributesLayoutConfig,
    },
    canActivate: [canAccess('attributes', 'manage')],
  },
  {
    path: baseRoutes.manageAttributes,
    loadComponent: () =>
      import(
        './containers/manage-attributes-container/manage-attributes-container.component'
      ).then((mod) => mod.DeprecatedManageAttributesContainerComponent),
    data: {
      layoutConfig: manageAttributesLayoutConfig,
    },
    canActivate: [canAccess('attributes', 'manage')],
  },
  {
    path: baseRoutes.manageData,
    loadComponent: () =>
      import(
        './containers/data-editor-container/data-editor-container.component'
      ).then((mod) => mod.DataEditorContainerComponent),
    data: {
      layoutConfig: dataManagementLayoutConfig,
    },
    canDeactivate: [
      (component: DataEditorContainerComponent) => component.canDeactivate(),
    ],
    canActivate: [canAccess('repositoryData', 'manage')],
  },
  {
    path: `${baseRoutes.manageData}/:populationId`,
    loadComponent: () =>
      import(
        './containers/data-editor-container/data-editor-container.component'
      ).then((mod) => mod.DataEditorContainerComponent),
    data: {
      layoutConfig: dataManagementLayoutConfig,
    },
    canDeactivate: [
      (component: DataEditorContainerComponent) => component.canDeactivate(),
    ],
    canActivate: [canAccess('repositoryData', 'manage')],
  },
  {
    path: baseRoutes.integrations,
    canMatch: [() => inject(FeatureFlagsStore).integrationsEnabled()],
    loadComponent: () =>
      import(
        './containers/integrations-container/integrations-container.component'
      ).then((mod) => mod.IntegrationsContainerComponent),
    data: {
      layoutConfig: dataManagementLayoutConfig,
    },
    canActivate: [canAccess('integrations', 'manage')],
  },
];
